import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {CopyBlock} from 'components/DSYS/CopyBlock';
import type {ClientGuide} from 'pages/mcpClientGuides';

/**
 * Renders the numbered copy/paste install steps for an MCP client guide. Shared
 * by the Settings MCP page and the workstream update assistant so the two stay
 * in sync (title + body, an optional CTA/action, a copyable code block, and a
 * capped-height screenshot).
 */
export const ClientInstallSteps = ({guide}: {guide: ClientGuide}) => (
  <Stack gap="16">
    {guide.steps.map((step, index) => {
      const showStepNumber = guide.steps.length > 1;
      const label = `${guide.name} step ${index + 1}`;
      return (
        <Split key={step.title} gap="12">
          {showStepNumber && (
            <Split.Item>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                backgroundColor="Primary Surface"
                borderRadius="Circular"
                style={{width: 24, height: 24, color: 'var(--dig-color__primary__base)'}}
              >
                <Text size="small" isBold color="inherit">
                  {index + 1}
                </Text>
              </Box>
            </Split.Item>
          )}
          <Split.Item width="fill">
            <Stack gap="8">
              <Box>
                <Text isBold>{step.title}</Text>
              </Box>
              {step.body && (
                <Box>
                  <Text color="subtle">{step.body}</Text>
                </Box>
              )}
              {step.action}
              {step.code && (
                <CopyBlock
                  code={step.code}
                  label={label}
                  onCopy={() => analyticsLogger().logEvent('MCP_SETUP_COPIED', {label})}
                />
              )}
              {step.image && (
                <img
                  src={step.image}
                  alt={step.imageAlt ?? ''}
                  className={atoms({
                    display: 'block',
                    marginTop: '8',
                    borderRadius: 'Medium',
                    borderStyle: 'Solid',
                    borderColor: 'Border Subtle',
                    borderWidth: '1',
                  })}
                  style={{maxWidth: '100%', maxHeight: 280, height: 'auto'}}
                />
              )}
            </Stack>
          </Split.Item>
        </Split>
      );
    })}
  </Stack>
);
